<script setup lang="ts">
    import { buildProductDetailPath, slugify, useRequiredProductSectionPath } from '#imports';
    import type { ProductItemGroup } from '@/types/censhare/productItemGroup';
    import { CENSHARE_PRODUCT_NOT_RELEASED } from '~/utils/CenshareClient/constants';
    import { getSlugForContext } from '@/utils/CenshareClient/slugMap';

    const props = defineProps<{ blok: FachkreisProductStoryblok }>();

    const headers = useRequestHeaders(['cookie']);
    const { country, urlSegment, lang } = useLanguage();
    const { isDraftModeEnabled } = useDraftMode();
    const { data, error } = await useFetch<ProductItemGroup>(`/api/censhare/productitemgroup/${props.blok.productId}`, {
        headers,
        query: {
            country: country.value,
            lang: lang.value
        }
    });

    type ProductItemGroupErrorData = {
        code?: string;
        missingFields?: string[];
    };

    const normalizedErrorData = computed(() => {
        const payload = error.value?.data as
            | undefined
            | {
                  code?: string;
                  data?: ProductItemGroupErrorData;
                  missingFields?: string[];
              };

        return {
            code: payload?.data?.code ?? payload?.code,
            missingFields: payload?.data?.missingFields ?? payload?.missingFields ?? []
        };
    });

    const errorReason = computed<'invalidId' | 'notReleased' | 'notFound' | undefined>(() => {
        if (!error.value) {
            return undefined;
        }

        if (
            normalizedErrorData.value.code === CENSHARE_PRODUCT_NOT_RELEASED ||
            normalizedErrorData.value.missingFields.includes('countryOfDistribution')
        ) {
            return 'notReleased';
        }

        if (!props.blok.productId?.toString().trim()) {
            return 'invalidId';
        }

        return 'notFound';
    });

    const productSectionPath = useRequiredProductSectionPath();

    const productCard = computed(() => {
        const product = data.value;

        return {
            hasProductData: Boolean(product),
            cardName: product?.nameLocalized?.[lang.value] ?? product?.name ?? '',
            cardImageId: product?.media?.mainPicture?.[0]?.id ?? null,
            isPrescriptionOnly: Boolean(product?.features?.prescriptionOnly?.value)
        };
    });

    const detailSlug = computed(() => {
        const currentData = data.value;
        if (!currentData) return props.blok.productId;

        const fallbackName = currentData.nameLocalized?.[lang.value] ?? currentData.name ?? props.blok.productId;
        return getSlugForContext(currentData.slugs, country.value, lang.value) || slugify(fallbackName);
    });

    const detailPath = computed(() =>
        buildProductDetailPath(urlSegment.value, productSectionPath.value, detailSlug.value)
    );
</script>

<template>
    <FachkreisProductCard
        v-if="!Boolean(error) && productCard.hasProductData"
        :name="productCard.cardName"
        :image-id="productCard.cardImageId"
        :is-prescription-only="productCard.isPrescriptionOnly"
        :link-to="detailPath"
    />

    <FachkreisProductCard
        v-if="Boolean(error) && !productCard.hasProductData && isDraftModeEnabled"
        :name="productCard.cardName"
        :image-id="productCard.cardImageId"
        :is-prescription-only="productCard.isPrescriptionOnly"
        :link-to="detailPath"
        :invalid-id="Boolean(error)"
        :error-reason="errorReason"
    />
</template>
